/**
 * Mooring-ring maths (M10 P1e1, hull tiers spec §6.4): where a hull too big for a station bay holds, loads and
 * launches. Pure float64 functions with out parameters; they allocate nothing (D-014), so the moor flow (P1e2) and
 * the AI traffic pilot (P1e3) can call them every tick.
 *
 * - **The ring** is a circle round the station centre of radius `max(ringRadiusM, stationRadius + ringClearM)`, in
 *   the plane spanned by the bay's out axis and the horizontal `right = out × worldUp` (world X when the out axis is
 *   vertical). Its normal (`right × out`) is the moored ship's up.
 * - **Slots.** The slot angle θ is measured from the out axis toward `right`. The `slots` slots spread evenly over
 *   [corridorClearDeg, 360° − corridorClearDeg], centred on the side away from the bay, so none lies within
 *   `corridorClearDeg` of the out axis and the approach corridor stays clear.
 * - A moored ship rests at its slot point with its nose along the ring tangent (`moorRingTangent`) and launches
 *   radially out (`moorRingPoint − stationPos`, normalised).
 */
import type { Vec3 } from '../core/math'
import type { MoorConfig } from './moorConfig'

export type RingConfig = Pick<MoorConfig, 'ringRadiusM' | 'ringClearM' | 'slots' | 'corridorClearDeg'>

/** Below this horizontal length the out axis counts as vertical. */
const VERTICAL = 1e-6
const DEG = Math.PI / 180

/** The ring radius round a station of bounding radius `stationRadius`. */
export function moorRingRadius(stationRadius: number, cfg: RingConfig): number {
  const clear = stationRadius + cfg.ringClearM
  return clear > cfg.ringRadiusM ? clear : cfg.ringRadiusM
}

/** Slot `slot`'s angle from the bay's out axis (radians). Slots wrap modulo `cfg.slots`. */
export function moorSlotAngle(slot: number, cfg: RingConfig): number {
  const n = cfg.slots
  const i = ((slot % n) + n) % n
  const clear = cfg.corridorClearDeg * DEG
  return clear + ((2 * Math.PI - 2 * clear) * (i + 0.5)) / n
}

/** The ring's in-plane axis at θ = 90°: unit `bayOut × worldUp`, or `bayOut × worldX` for a vertical out axis. Into `out`. */
export function moorRingRight(bayOut: Readonly<Vec3>, out: Vec3): Vec3 {
  const h = Math.sqrt(bayOut.x * bayOut.x + bayOut.z * bayOut.z)
  if (h > VERTICAL) {
    out.x = -bayOut.z / h
    out.y = 0
    out.z = bayOut.x / h
    return out
  }
  const v = Math.sqrt(bayOut.y * bayOut.y + bayOut.z * bayOut.z)
  out.x = 0
  out.y = bayOut.z / v
  out.z = -bayOut.y / v
  return out
}

/** The ring plane's unit normal, `right × out` (the moored ship's up; world up for a level bay). Into `out`. */
export function moorRingUp(bayOut: Readonly<Vec3>, out: Vec3): Vec3 {
  moorRingRight(bayOut, out)
  const rx = out.x
  const ry = out.y
  const rz = out.z
  const l = Math.sqrt(bayOut.x * bayOut.x + bayOut.y * bayOut.y + bayOut.z * bayOut.z)
  const ox = bayOut.x / l
  const oy = bayOut.y / l
  const oz = bayOut.z / l
  out.x = ry * oz - rz * oy
  out.y = rz * ox - rx * oz
  out.z = rx * oy - ry * ox
  return out
}

/**
 * The unit direction from the station centre to slot `slot` (cos θ × out + sin θ × right). Into `out`.
 * `bayOut` need not be unit length.
 */
export function moorSlotDirection(bayOut: Readonly<Vec3>, slot: number, cfg: RingConfig, out: Vec3): Vec3 {
  moorRingRight(bayOut, out)
  const theta = moorSlotAngle(slot, cfg)
  const c = Math.cos(theta)
  const s = Math.sin(theta)
  const l = Math.sqrt(bayOut.x * bayOut.x + bayOut.y * bayOut.y + bayOut.z * bayOut.z)
  out.x = (c * bayOut.x) / l + s * out.x
  out.y = (c * bayOut.y) / l + s * out.y
  out.z = (c * bayOut.z) / l + s * out.z
  return out
}

/**
 * Slot `slot`'s ring point (float64 world metres) round a station at `stationPos` with bounding radius
 * `stationRadius` and bay out axis `bayOut`. Into `out`.
 */
export function moorRingPoint(
  stationPos: Readonly<Vec3>,
  stationRadius: number,
  bayOut: Readonly<Vec3>,
  slot: number,
  cfg: RingConfig,
  out: Vec3,
): Vec3 {
  const r = moorRingRadius(stationRadius, cfg)
  moorSlotDirection(bayOut, slot, cfg, out)
  out.x = stationPos.x + r * out.x
  out.y = stationPos.y + r * out.y
  out.z = stationPos.z + r * out.z
  return out
}

/** The unit ring tangent at slot `slot` (toward rising θ: −sin θ × out + cos θ × right): a moored ship's nose. Into `out`. */
export function moorRingTangent(bayOut: Readonly<Vec3>, slot: number, cfg: RingConfig, out: Vec3): Vec3 {
  moorRingRight(bayOut, out)
  const theta = moorSlotAngle(slot, cfg)
  const c = Math.cos(theta)
  const s = Math.sin(theta)
  const l = Math.sqrt(bayOut.x * bayOut.x + bayOut.y * bayOut.y + bayOut.z * bayOut.z)
  out.x = (-s * bayOut.x) / l + c * out.x
  out.y = (-s * bayOut.y) / l + c * out.y
  out.z = (-s * bayOut.z) / l + c * out.z
  return out
}
